"use client";

import { type ReactNode, useState } from "react";
import { PillGroup, TextAreaField } from "@/components/tool-kit/primitives";
import { TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import { parseNumberList } from "@/lib/parse-number";
import { parsePairLines } from "@/lib/tool-kit/stats-descriptive/inputs";

/*
 * Presentational pieces shared by the descriptive-statistics widgets
 * (`src/data/tool-families/stats-descriptive.ts`).
 *
 * Like the tool kit's primitives, these own layout and behaviour, never vocabulary:
 * every label arrives through props from the calling widget's own `*_STRINGS`, so a
 * translated content file reaches every word.
 */

/** The "how this was calculated" box under a result. */
export function WorkingPanel({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4 sm:p-6">
      <h3 className={WIDGET_H3}>{title}</h3>
      {children}
    </div>
  );
}

/** One line of working: a formula with the reader's numbers substituted in. */
export function Formula({ children }: { children: ReactNode }) {
  return (
    <p className="break-words font-mono text-muted-foreground text-sm leading-relaxed">
      {children}
    </p>
  );
}

/** A sentence of interpretation inside a working panel. */
export function Explanation({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground text-sm leading-relaxed">{children}</p>
  );
}

/**
 * A results table that scrolls sideways instead of squeezing.
 *
 * The page body sets `overflow-wrap: anywhere`, which lets a wide table shrink its
 * columns until "Frequency" breaks as "FREQ/UEN/CY". This table restores normal
 * wrapping — headers wrap between words, numbers never wrap — and the wrapper scrolls
 * horizontally when the columns need more room than the page has.
 */
/**
 * The kit's header style, minus its uppercase transform: these headers are formulas,
 * and uppercasing turns x − x̄ into X − X̄, a different symbol.
 */
const MATH_TH = `${TH.replace("uppercase tracking-wide", "")} align-bottom`;

export function ResultTable({
  head,
  rows,
  footer,
  caption,
  wrapFirstColumn = false,
}: {
  head: readonly string[];
  rows: readonly (readonly string[])[];
  /** A totals row, set apart in the table footer. */
  footer?: readonly string[];
  caption?: string;
  /** Let a long text label in the first column wrap between words. */
  wrapFirstColumn?: boolean;
}) {
  const cellClass = (index: number) =>
    wrapFirstColumn && index === 0
      ? `${TD} min-w-40`
      : `${TD} whitespace-nowrap tabular-nums`;
  const footClass = (index: number) => `${cellClass(index)} font-semibold`;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={`${TABLE} [overflow-wrap:normal]`}>
        {caption ? (
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {caption}
          </caption>
        ) : null}
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={MATH_TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // Rows are positional data with no identity of their own.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                <td className={cellClass(cellIndex)} key={cellIndex}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer ? (
          <tfoot>
            <tr>
              {footer.map((cell, cellIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                <td className={footClass(cellIndex)} key={cellIndex}>
                  {cell}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

export type PairMode = "lists" | "lines";

export interface PairedDataLabels {
  mode: string;
  lists: string;
  lines: string;
  x: string;
  y: string;
  xPlaceholder: string;
  yPlaceholder: string;
  pairs: string;
  pairsPlaceholder: string;
}

/**
 * Paired (x, y) data typed as two lists or as one "x, y" pair per line — the two ways
 * `correlation-calculator` accepts it, so the statistics tools read pairs one way.
 */
export function usePairedData() {
  const [mode, setMode] = useState<PairMode>("lists");
  const [xText, setXText] = useState("");
  const [yText, setYText] = useState("");
  const [pairsText, setPairsText] = useState("");

  const listXs = parseNumberList(xText);
  const listYs = parseNumberList(yText);
  const lines = parsePairLines(pairsText);
  const xs = mode === "lists" ? listXs : lines.xs;
  const ys = mode === "lists" ? listYs : lines.ys;

  return {
    mode,
    setMode,
    xText,
    setXText,
    yText,
    setYText,
    pairsText,
    setPairsText,
    xs,
    ys,
    listXCount: listXs.length,
    listYCount: listYs.length,
    pairCount: lines.xs.length,
    skippedLines: lines.skipped,
    /** The two lists disagree in length — the pairing is ambiguous. */
    mismatched: mode === "lists" && listXs.length !== listYs.length,
    hasInput: xs.length > 0 || ys.length > 0,
    fill(example: { x: string; y: string; pairs: string }) {
      setXText(example.x);
      setYText(example.y);
      setPairsText(example.pairs);
    },
    clear() {
      setXText("");
      setYText("");
      setPairsText("");
    },
  };
}

export type PairedData = ReturnType<typeof usePairedData>;

export function PairedDataInput({
  idPrefix,
  data,
  labels,
  xHint,
  yHint,
  pairsHint,
}: {
  idPrefix: string;
  data: PairedData;
  labels: PairedDataLabels;
  xHint: ReactNode;
  yHint: ReactNode;
  pairsHint: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <PillGroup
        label={labels.mode}
        name={`${idPrefix}-entry`}
        onChange={data.setMode}
        options={[
          { value: "lists", label: labels.lists },
          { value: "lines", label: labels.lines },
        ]}
        value={data.mode}
      />
      {data.mode === "lists" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={xHint}
            id={`${idPrefix}-x`}
            label={labels.x}
            onChange={data.setXText}
            placeholder={labels.xPlaceholder}
            value={data.xText}
          />
          <TextAreaField
            hint={yHint}
            id={`${idPrefix}-y`}
            label={labels.y}
            onChange={data.setYText}
            placeholder={labels.yPlaceholder}
            value={data.yText}
          />
        </div>
      ) : (
        <TextAreaField
          hint={pairsHint}
          id={`${idPrefix}-pairs`}
          label={labels.pairs}
          onChange={data.setPairsText}
          placeholder={labels.pairsPlaceholder}
          value={data.pairsText}
        />
      )}
    </div>
  );
}
